import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from './config';
import { RouteVideo } from '../../models/climbing/ClimbingRoute';
import { Gym } from '../../models/gym/Gym';

/**
 * Add a video to a climbing route stored in gym document
 */
export const addVideoToRoute = async (
  gymId: string,
  routeId: string,
  videoData: {
    url: string;
    title?: string;
    uploadedBy: string;
    uploadedByName?: string;
  }
): Promise<void> => {
  try {
    const gymRef = doc(db, 'gyms', gymId);
    
    // Get current gym document
    const gymSnap = await getDoc(gymRef);
    if (!gymSnap.exists()) {
      throw new Error('Gym not found');
    }

    const gymData = gymSnap.data();
    const climbing_routes = gymData.climbing_routes || {};
    
    // Check if route exists
    if (!climbing_routes[routeId]) {
      throw new Error('Route not found');
    }

    // Create the video object
    const video: RouteVideo = {
      id: `video_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      url: videoData.url,
      title: videoData.title,
      uploadedBy: videoData.uploadedBy,
      uploadedByName: videoData.uploadedByName,
      uploadedAt: Date.now()
    };

    // Get current videos array or create empty array
    const currentVideos = climbing_routes[routeId].videos || [];
    
    // Add new video to the array
    const updatedVideos = [...currentVideos, video];
    
    // Update the specific route's videos in the gym document
    await updateDoc(gymRef, {
      [`climbing_routes.${routeId}.videos`]: updatedVideos
    });

    console.log('Video added to route successfully');
  } catch (error) {
    console.error('Error adding video to route:', error);
    throw error;
  }
};

/**
 * Remove a video from a climbing route stored in gym document
 */
export const removeVideoFromRoute = async (
  gymId: string,
  routeId: string,
  videoId: string,
  userId: string
): Promise<void> => {
  try {
    const gymRef = doc(db, 'gyms', gymId);
    
    // Get current gym document
    const gymSnap = await getDoc(gymRef);
    if (!gymSnap.exists()) {
      throw new Error('Gym not found');
    }

    const gymData = gymSnap.data();
    const climbing_routes = gymData.climbing_routes || {};
    
    // Check if route exists
    if (!climbing_routes[routeId]) {
      throw new Error('Route not found');
    }

    const videos = climbing_routes[routeId].videos || [];
    
    // Find the video to remove and check permissions
    const videoToRemove = videos.find((v: RouteVideo) => v.id === videoId);
    if (!videoToRemove) {
      throw new Error('Video not found');
    }

    // Only allow the uploader to remove their own video
    if (videoToRemove.uploadedBy !== userId) {
      throw new Error('You can only remove your own videos');
    }

    // Remove the video from the array
    const updatedVideos = videos.filter((v: RouteVideo) => v.id !== videoId);
    
    // Update the specific route's videos in the gym document
    await updateDoc(gymRef, {
      [`climbing_routes.${routeId}.videos`]: updatedVideos
    });

    console.log('Video removed from route successfully');
  } catch (error) {
    console.error('Error removing video from route:', error);
    throw error;
  }
};

/**
 * Get all videos for a route stored in gym document
 */
export const getRouteVideos = async (
  gymId: string,
  routeId: string
): Promise<RouteVideo[]> => {
  try {
    const gymRef = doc(db, 'gyms', gymId);
    const gymSnap = await getDoc(gymRef);
    
    if (!gymSnap.exists()) {
      return [];
    }

    const gymData = gymSnap.data();
    const climbing_routes = gymData.climbing_routes || {};
    
    if (!climbing_routes[routeId]) {
      return [];
    }

    return climbing_routes[routeId].videos || [];
  } catch (error) {
    console.error('Error getting route videos:', error);
    return [];
  }
};
